/**
 * Centralized Color System
 * Single source of truth for all colors used across the template
 */

export const colorSystem = {
  // Primary brand color - WVON Royal Blue
  primary: '#1E40AF',
  
  // Neutral colors
  black: '#000000',
  white: '#FFFFFF',
  
  // Background gradients - Updated WVON Blue Gradient
  darkGradient: {
    start: '#1858FA', // Light Blue
    end: '#162B61',   // Dark Blue
    angle: 180
  },
  lightGradient: {
    start: '#f8f9fa',
    end: '#e9ecef', 
    angle: 135
  },
  
  // Semantic colors
  success: '#22c55e',
  error: '#ef4444',
  warning: '#f59e0b',
  info: '#3b82f6',
  
  // Opacity variants for consistent transparency
  black10: 'rgba(0, 0, 0, 0.1)',
  black15: 'rgba(0, 0, 0, 0.15)',
  black20: 'rgba(0, 0, 0, 0.2)',
  black50: 'rgba(0, 0, 0, 0.5)',
  black70: 'rgba(0, 0, 0, 0.7)',
  
  white5: 'rgba(255, 255, 255, 0.05)',
  white10: 'rgba(255, 255, 255, 0.1)',
  white15: 'rgba(255, 255, 255, 0.15)',
  white50: 'rgba(255, 255, 255, 0.5)',
  white70: 'rgba(255, 255, 255, 0.7)',
  
  // Gray scale for consistent neutral tones
  gray50: '#f9fafb',
  gray100: '#f3f4f6',
  gray200: '#e5e7eb',
  gray300: '#d1d5db',
  gray400: '#9ca3af',
  gray500: '#6b7280',
  gray600: '#4b5563',
  gray700: '#374151',
  gray800: '#1f2937',
  gray900: '#111827'
};

/**
 * Generate theme objects with consistent color usage
 */
export function generateThemeObjects(primaryColor: string = colorSystem.primary) {
  // Extract RGB values from hex color

  return {
    darkPrimary: {
      text: colorSystem.white,
      mutedText: colorSystem.white70,
      subtleText: colorSystem.white50,
      accent: primaryColor,
      button: primaryColor,
      onButton: '#FFDD00', // CTA button text color
      card: colorSystem.white5,
      border: colorSystem.white10,
      borderHover: colorSystem.white15,
      input: colorSystem.white10,
      surface: 'dark'
    },
    
    lightPrimary: {
      text: colorSystem.black,
      mutedText: colorSystem.black70,
      subtleText: colorSystem.black50,
      accent: primaryColor,
      button: primaryColor,
      onButton: '#FFDD00', // CTA button text color
      card: colorSystem.white,
      border: colorSystem.black10,
      borderHover: colorSystem.black15,
      input: colorSystem.gray100,
      surface: 'light'
    }
  };
}

/**
 * Create gradient style object
 */
export function createGradientStyle() {
  // Use the new 3-color WVON gradient
  return {
    background: `linear-gradient(90deg, rgba(2, 0, 36, 1) 0%, rgba(9, 41, 121, 1) 35%, rgba(82, 66, 7, 1) 100%)`
  };
}

/**
 * Validate color accessibility (basic contrast check)
 */
export function validateColorContrast(foreground: string, background: string): boolean {
  // Basic validation - in production, use a proper contrast checker
  const isLightBackground = background === colorSystem.white || 
                           background.includes('248, 249, 250') ||
                           background.includes('#f8f9fa');
  
  const isDarkForeground = foreground === colorSystem.black ||
                          foreground.includes('0, 0, 0');
  
  const isLightForeground = foreground === colorSystem.white ||
                           foreground.includes('255, 255, 255');
  
  if (isLightBackground && isDarkForeground) return true;
  if (!isLightBackground && isLightForeground) return true;
  
  return false;
}

/**
 * Get CSS custom properties for runtime color changes
 */
export function getCSSVariables(primaryColor: string = colorSystem.primary) {
  return {
    '--color-primary': primaryColor,
    '--color-black': colorSystem.black,
    '--color-white': colorSystem.white,
    '--color-success': colorSystem.success,
    '--color-error': colorSystem.error,
    '--color-warning': colorSystem.warning,
    '--color-info': colorSystem.info,
    '--gradient-dark-start': colorSystem.darkGradient.start,
    '--gradient-dark-end': colorSystem.darkGradient.end,
    '--gradient-light-start': colorSystem.lightGradient.start,
    '--gradient-light-end': colorSystem.lightGradient.end,
  };
}

/**
 * Calculate relative luminance using W3C formula with gamma correction
 * Returns "black" or "white" for optimal text contrast on CTA buttons
 */
export function getContrastTextColor(r: number, g: number, b: number): "black" | "white" {
  // Normalize each channel by dividing by 255
  const normalizeChannel = (channel: number): number => {
    const normalized = channel / 255;
    
    // Apply gamma correction
    if (normalized <= 0.03928) {
      return normalized / 12.92;
    } else {
      return Math.pow((normalized + 0.055) / 1.055, 2.4);
    }
  };

  // Calculate relative luminance
  const luminance = 0.2126 * normalizeChannel(r) + 
                   0.7152 * normalizeChannel(g) + 
                   0.0722 * normalizeChannel(b);

  // Return text color based on luminance threshold
  return luminance > 0.179 ? "black" : "white";
}
